import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDown, CheckCircle, XCircle, Clock, Flame } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;
const statusColors = {
  pending: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
  approved: "text-[#22C55E] bg-[#22C55E]/10 border-[#22C55E]/30",
  rejected: "text-red-400 bg-red-400/10 border-red-400/30",
};

export default function AdminApplications() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterRole, setFilterRole] = useState("all");
  const [showInterestedOnly, setShowInterestedOnly] = useState(false);
  // Server truth: only treat the session as demo when /api/auth/me explicitly
  // reports is_demo=true. localStorage is a UX hint only and is NOT trusted
  // for control gating — fixes a stale flag from a previous demo visit
  // disabling admin operator controls on real-admin login.
  const isDemo = user?.is_demo === true;

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/applications`, { withCredentials: true });
      setApps(data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/admin/applications/${id}/status`, { status }, { withCredentials: true });
    fetch();
  };

  const filtered = apps.filter((a) => {
    if (filterStatus !== "all" && a.status !== filterStatus) return false;
    if (filterRole !== "all" && a.role !== filterRole) return false;
    if (showInterestedOnly && !a.demo_interest) return false;
    return true;
  });

  return (
    <div data-testid="admin-applications">
      <h1 className="font-['Outfit'] text-2xl font-bold text-white mb-6">Applications</h1>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[140px] bg-[#09120D] border-[#1A2E22] text-white text-sm h-9" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#09120D] border-[#1A2E22] text-white">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-[140px] bg-[#09120D] border-[#1A2E22] text-white text-sm h-9" data-testid="filter-role">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent className="bg-[#09120D] border-[#1A2E22] text-white">
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="buyer">Importer / Buyer</SelectItem>
            <SelectItem value="supplier">Exporter</SelectItem>
            <SelectItem value="downstream">Downstream Buyer</SelectItem>
            <SelectItem value="investor">Investor</SelectItem>
          </SelectContent>
        </Select>
        <button
          onClick={() => setShowInterestedOnly((v) => !v)}
          className={`text-xs px-3 py-1.5 border h-9 inline-flex items-center gap-1.5 transition-colors ${showInterestedOnly ? "border-orange-400/50 bg-orange-400/10 text-orange-400" : "border-[#1A2E22] text-[#A1B2A8] hover:text-white"}`}
          data-testid="filter-interested"
          title="Show only applicants who explicitly requested demo access"
        >
          <Flame className="w-3.5 h-3.5" /> Demo-interested only
          <span className={`ml-1 px-1.5 py-0.5 text-[10px] ${showInterestedOnly ? "bg-orange-400/20" : "bg-[#1A2E22]"}`} data-testid="interested-count">
            {apps.filter((a) => a.demo_interest).length}
          </span>
        </button>
        <span className="text-[#A1B2A8]/50 text-xs ml-auto">{filtered.length} of {apps.length}</span>
      </div>

      <div className="border border-[#1A2E22] overflow-x-auto">
        <table className="w-full" data-testid="applications-table">
          <thead>
            <tr className="border-b border-[#1A2E22] bg-[#09120D]">
              <th className="text-left py-3 px-4 text-[#A1B2A8] text-xs font-medium uppercase tracking-wider">Applicant</th>
              <th className="text-left py-3 px-4 text-[#A1B2A8] text-xs font-medium uppercase tracking-wider">Company</th>
              <th className="text-left py-3 px-4 text-[#A1B2A8] text-xs font-medium uppercase tracking-wider">Role</th>
              <th className="text-left py-3 px-4 text-[#A1B2A8] text-xs font-medium uppercase tracking-wider">Volume / Ticket</th>
              <th className="text-left py-3 px-4 text-[#A1B2A8] text-xs font-medium uppercase tracking-wider">Status</th>
              <th className="text-left py-3 px-4 text-[#A1B2A8] text-xs font-medium uppercase tracking-wider">Date</th>
              <th className="text-left py-3 px-4 text-[#A1B2A8] text-xs font-medium uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="py-12 text-center text-[#A1B2A8] text-sm">No applications</td></tr>
            ) : filtered.map((a) => (
              <tr key={a.id} className="border-b border-[#1A2E22] hover:bg-[#0D1B13]/50 transition-colors" data-testid={`app-row-${a.id}`}>
                <td className="py-4 px-4">
                  <div className="flex items-center gap-2">
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium flex items-center gap-1.5">
                        {a.full_name}
                        {a.demo_interest && (
                          <span title="Explicitly requested demo access" data-testid={`interest-flag-${a.id}`}>
                            <Flame className="w-3.5 h-3.5 text-orange-400" />
                          </span>
                        )}
                      </p>
                      <p className="text-[#A1B2A8] text-xs">{a.email}</p>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4 text-[#A1B2A8] text-sm">{a.company}</td>
                <td className="py-4 px-4"><span className={`text-xs capitalize px-2 py-1 border ${
                  a.role === "buyer" ? "text-blue-400 bg-blue-400/10 border-blue-400/30"
                  : a.role === "downstream" ? "text-cyan-400 bg-cyan-400/10 border-cyan-400/30"
                  : a.role === "investor" ? "text-[#22C55E] bg-[#22C55E]/10 border-[#22C55E]/30"
                  : "text-purple-400 bg-purple-400/10 border-purple-400/30"
                }`}>{a.role}</span></td>
                <td className="py-4 px-4 text-white text-xs font-mono" data-testid={`app-volume-${a.id}`}>{a.annual_volume || <span className="text-[#A1B2A8]/50">—</span>}</td>
                <td className="py-4 px-4"><span className={`text-xs capitalize px-2 py-1 border ${statusColors[a.status] || statusColors.pending}`}>{a.status}</span></td>
                <td className="py-4 px-4 text-[#A1B2A8] text-xs">{new Date(a.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</td>
                <td className="py-4 px-4">
                  {isDemo ? (
                    <span className="text-[#A1B2A8]/40 text-[10px] uppercase tracking-wider" data-testid={`action-readonly-${a.id}`}>Read-only</span>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="text-[#A1B2A8] hover:text-white text-xs border border-[#1A2E22] px-3 py-1.5 flex items-center gap-1" data-testid={`action-btn-${a.id}`}>
                          Action <ChevronDown className="w-3 h-3" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#09120D] border-[#1A2E22] text-white">
                        <DropdownMenuItem onClick={() => updateStatus(a.id, "approved")} className="text-[#22C55E] focus:bg-[#22C55E]/10 cursor-pointer"><CheckCircle className="w-3.5 h-3.5 mr-2" />Approve</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => updateStatus(a.id, "rejected")} className="text-red-400 focus:bg-red-400/10 cursor-pointer"><XCircle className="w-3.5 h-3.5 mr-2" />Reject</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => updateStatus(a.id, "pending")} className="text-yellow-400 focus:bg-yellow-400/10 cursor-pointer"><Clock className="w-3.5 h-3.5 mr-2" />Pending</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
